import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react'
import { AnalyticsConsentCard, AnalyticsPreferences } from './analytics/AnalyticsConsent'
import { HomepageV3Footer, HomepageV3Header } from './components/HomepageV3Chrome'
import allergensMarkdown from './content/legal/allergens-v1.0.md?raw'
import deliveryMarkdown from './content/legal/delivery-v1.0.md?raw'
import legalHubMarkdown from './content/legal/legal-hub-v1.0.md?raw'
import paymentMarkdown from './content/legal/payment-v1.1.md?raw'
import privacyMarkdown from './content/legal/privacy-v1.10.md?raw'
import refundsMarkdown from './content/legal/refunds-v1.0.md?raw'
import termsMarkdown from './content/legal/terms-v1.3.md?raw'
import { cartBoxCount } from './order/cartStorage'

type LegalRoute = '/legal' | '/terms' | '/refunds' | '/delivery-terms' | '/payment-terms' | '/privacy' | '/allergens'

type MarkdownBlock =
  | { type: 'heading'; level: 1 | 2 | 3; text: string }
  | { type: 'paragraph'; lines: string[] }
  | { type: 'unordered-list'; items: string[] }
  | { type: 'ordered-list'; items: string[] }

const HUB_LINKS: Record<string, LegalRoute> = {
  'Terms & Conditions': '/terms',
  'Refund, Replacement & Cancellation Policy': '/refunds',
  'Delivery Terms': '/delivery-terms',
  'Payment Terms': '/payment-terms',
  'Privacy Policy': '/privacy',
  'Food & Allergen Notice': '/allergens',
}

const SOURCE_METADATA_PATTERN = /^\*\*(Status|Version|Last reviewed|Effective date):\s*(.*?)\*\*$/

function parseMarkdown(source: string): MarkdownBlock[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n')
  const blocks: MarkdownBlock[] = []
  let index = 0

  while (index < lines.length) {
    const line = lines[index]
    if (!line.trim()) {
      index += 1
      continue
    }

    const heading = line.match(/^(#{1,3})\s+(.+)$/)
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length as 1 | 2 | 3, text: heading[2].trim() })
      index += 1
      continue
    }

    if (line.startsWith('- ')) {
      const items: string[] = []
      while (index < lines.length && lines[index].startsWith('- ')) {
        items.push(lines[index].slice(2).trim())
        index += 1
      }
      blocks.push({ type: 'unordered-list', items })
      continue
    }

    if (/^\d+\.\s/.test(line)) {
      const items: string[] = []
      while (index < lines.length && /^\d+\.\s/.test(lines[index])) {
        items.push(lines[index].replace(/^\d+\.\s/, '').trim())
        index += 1
      }
      blocks.push({ type: 'ordered-list', items })
      continue
    }

    const paragraphLines: string[] = []
    while (
      index < lines.length
      && lines[index].trim()
      && !/^(#{1,3})\s+/.test(lines[index])
      && !lines[index].startsWith('- ')
      && !/^\d+\.\s/.test(lines[index])
    ) {
      paragraphLines.push(lines[index].replace(/\s{2}$/, ''))
      index += 1
    }
    blocks.push({ type: 'paragraph', lines: paragraphLines })
  }

  return blocks
}

function linkify(value: string, keyPrefix: string): ReactNode[] {
  const pattern = /(hello@sugargremlin\.co\.nz|sugargremlin\.co\.nz(?:\/[a-z0-9-]+)?)/gi
  const nodes: ReactNode[] = []
  let cursor = 0
  let match: RegExpExecArray | null

  while ((match = pattern.exec(value)) !== null) {
    if (match.index > cursor) nodes.push(value.slice(cursor, match.index))
    const token = match[0]
    const lower = token.toLowerCase()
    const href = lower.includes('@')
      ? `mailto:${token}`
      : lower === 'sugargremlin.co.nz'
        ? 'https://sugargremlin.co.nz'
        : `/${token.split('/').slice(1).join('/')}`
    nodes.push(<a href={href} key={`${keyPrefix}-${match.index}`}>{token}</a>)
    cursor = pattern.lastIndex
  }

  if (cursor < value.length) nodes.push(value.slice(cursor))
  return nodes
}

function inlineMarkdown(value: string, keyPrefix: string): ReactNode[] {
  const tokenPattern = /(\[Volley’s Privacy Policy\]\(https:\/\/volley\.nz\/privacy-policy\/\)|\*\*[^*]+\*\*|`[^`]+`)/g
  const output: ReactNode[] = []
  let cursor = 0
  let match: RegExpExecArray | null

  while ((match = tokenPattern.exec(value)) !== null) {
    if (match.index > cursor) output.push(...linkify(value.slice(cursor, match.index), `${keyPrefix}-plain-${match.index}`))
    const token = match[0]
    if (token === '[Volley’s Privacy Policy](https://volley.nz/privacy-policy/)') {
      output.push(<a key={`${keyPrefix}-volley-${match.index}`} href="https://volley.nz/privacy-policy/">Volley’s Privacy Policy</a>)
    } else if (token.startsWith('**')) {
      output.push(<strong key={`${keyPrefix}-strong-${match.index}`}>{linkify(token.slice(2, -2), `${keyPrefix}-strong-link-${match.index}`)}</strong>)
    } else {
      output.push(<code key={`${keyPrefix}-code-${match.index}`}>{token.slice(1, -1)}</code>)
    }
    cursor = tokenPattern.lastIndex
  }

  if (cursor < value.length) output.push(...linkify(value.slice(cursor), `${keyPrefix}-tail`))
  return output
}

const META_MEASUREMENT_HEADING = 'META PIXEL AND CONVERSIONS API ADVERTISING MEASUREMENT'

function LegalMarkdown({ source, hub = false, analyticsPreferences = false }: { source: string; hub?: boolean; analyticsPreferences?: boolean }) {
  const blocks = useMemo(() => parseMarkdown(source), [source])
  let sourceTopLevelHeadingIndex = 0
  let visibleTopLevelHeadingSeen = false
  let activeSectionHeading: string | null = null

  const analyticsPreferencesBlock = (key: string) => (
    <div className="legal-doc-page__analytics" key={key}>
      <AnalyticsPreferences />
    </div>
  )

  return (
    <>
      {blocks.map((block, index) => {
        const insertPreferencesBefore = block.type === 'heading'
          && block.level === 2
          && analyticsPreferences
          && activeSectionHeading === META_MEASUREMENT_HEADING

        if (block.type === 'heading' && block.level === 2) {
          activeSectionHeading = block.text
        }

        let rendered: ReactNode = null

        if (block.type === 'heading') {
          if (block.level === 1) {
            const currentSourceHeadingIndex = sourceTopLevelHeadingIndex
            sourceTopLevelHeadingIndex += 1

            if (!(hub && currentSourceHeadingIndex === 0)) {
              const isFirstVisibleTopLevelHeading = !visibleTopLevelHeadingSeen
              visibleTopLevelHeadingSeen = true

              rendered = isFirstVisibleTopLevelHeading
                ? <h1 key={`h1-${index}`}>{inlineMarkdown(block.text, `h1-${index}`)}</h1>
                : <h2 className="legal-doc__secondary-title" key={`h1-secondary-${index}`}>{inlineMarkdown(block.text, `h1-secondary-${index}`)}</h2>
            }
          } else if (block.level === 2) {
            const href = hub ? HUB_LINKS[block.text] : undefined
            rendered = (
              <h2 className={href ? 'legal-doc__linked-heading' : undefined} key={`h2-${index}`}>
                {href ? <a href={href}>{inlineMarkdown(block.text, `h2-${index}`)} <span aria-hidden="true">→</span></a> : inlineMarkdown(block.text, `h2-${index}`)}
              </h2>
            )
          } else {
            rendered = <h3 key={`h3-${index}`}>{inlineMarkdown(block.text, `h3-${index}`)}</h3>
          }
        } else if (block.type === 'unordered-list') {
          rendered = <ul key={`ul-${index}`}>{block.items.map((item, itemIndex) => <li key={`${index}-${itemIndex}`}>{inlineMarkdown(item, `ul-${index}-${itemIndex}`)}</li>)}</ul>
        } else if (block.type === 'ordered-list') {
          rendered = <ol key={`ol-${index}`}>{block.items.map((item, itemIndex) => <li key={`${index}-${itemIndex}`}>{inlineMarkdown(item, `ol-${index}-${itemIndex}`)}</li>)}</ol>
        } else {
          const metadataMatches = block.lines.map((line) => line.trim().match(SOURCE_METADATA_PATTERN))
          const isSourceMetadataBlock = metadataMatches.length > 0 && metadataMatches.every((match) => match !== null)

          if (isSourceMetadataBlock) {
            if (!hub) {
              const version = metadataMatches.find((match) => match?.[1] === 'Version')?.[2] ?? ''
              const lastReviewed = metadataMatches.find((match) => match?.[1] === 'Last reviewed')?.[2] ?? ''
              const effectiveDate = metadataMatches.find((match) => match?.[1] === 'Effective date')?.[2] ?? ''

              rendered = lastReviewed || version || effectiveDate
                ? (
                  <p className="legal-doc__policy-meta" key={`meta-${index}`}>
                    {lastReviewed ? <>Last updated: {lastReviewed}</> : null}
                    {version ? <>{lastReviewed ? ' · ' : ''}Version {version}</> : null}
                    {effectiveDate ? <><br />Effective date: {effectiveDate}</> : null}
                  </p>
                )
                : null
            }
          } else {
            rendered = (
              <p key={`p-${index}`}>
                {block.lines.map((line, lineIndex) => (
                  <Fragment key={`${index}-${lineIndex}`}>
                    {inlineMarkdown(line, `p-${index}-${lineIndex}`)}
                    {lineIndex < block.lines.length - 1 ? <br /> : null}
                  </Fragment>
                ))}
              </p>
            )
          }
        }

        return insertPreferencesBefore
          ? (
            <Fragment key={`block-${index}`}>
              {analyticsPreferencesBlock(`analytics-${index}`)}
              {rendered}
            </Fragment>
          )
          : rendered
      })}
      {analyticsPreferences && activeSectionHeading === META_MEASUREMENT_HEADING
        ? analyticsPreferencesBlock('analytics-final')
        : null}
    </>
  )
}

function LegalDocumentPage({ source, route, hub = false, analyticsPreferences = false }: { source: string; route: LegalRoute; hub?: boolean; analyticsPreferences?: boolean }) {
  const [cartCount, setCartCount] = useState(() => cartBoxCount())

  useEffect(() => {
    const refreshCart = () => setCartCount(cartBoxCount())
    window.addEventListener('storage', refreshCart)
    const timer = window.setInterval(refreshCart, 500)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('storage', refreshCart)
    }
  }, [])

  return (
    <div className={`sg-v3 legal-doc-page${hub ? ' legal-doc-page--hub' : ''}`}>
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <HomepageV3Header cartCount={cartCount} activeHref={route} />
      <main className="legal-doc-page__main" id="main-content" tabIndex={-1}>
        <article className="sg-v3-shell legal-doc-page__content">
          <LegalMarkdown source={source} hub={hub} analyticsPreferences={analyticsPreferences} />
        </article>
      </main>
      <HomepageV3Footer />
      <AnalyticsConsentCard />
    </div>
  )
}

export function LegalHubPage() {
  return <LegalDocumentPage source={legalHubMarkdown} route="/legal" hub />
}

export function TermsPage() {
  return <LegalDocumentPage source={termsMarkdown} route="/terms" />
}

export function RefundsPage() {
  return <LegalDocumentPage source={refundsMarkdown} route="/refunds" />
}

export function DeliveryTermsPage() {
  return <LegalDocumentPage source={deliveryMarkdown} route="/delivery-terms" />
}

export function PaymentTermsPage() {
  return <LegalDocumentPage source={paymentMarkdown} route="/payment-terms" />
}

export function PrivacyPolicyPage() {
  return <LegalDocumentPage source={privacyMarkdown} route="/privacy" analyticsPreferences />
}

export function AllergensPage() {
  return <LegalDocumentPage source={allergensMarkdown} route="/allergens" />
}
